import snakecaseKeys from 'snakecase-keys';
import { toChainFormat } from '@injectivelabs/utils';
import { GoogleProtobufAny, CosmosGovV1Beta1Tx, CosmosBaseV1Beta1Coin, InjectiveExchangeV2Proposal, InjectiveOracleV1Beta1Oracle, } from '@injectivelabs/core-proto-ts';
import { MsgBase } from '../../MsgBase.js';
import { numberToCosmosSdkDecString } from '../../../../utils/numbers.js';
const createPerpetualMarketLaunch = (params) => {
    const content = InjectiveExchangeV2Proposal.PerpetualMarketLaunchProposal.create();
    content.title = params.market.title;
    content.description = params.market.description;
    content.ticker = params.market.ticker;
    content.quoteDenom = params.market.quoteDenom;
    content.oracleBase = params.market.oracleBase;
    content.oracleQuote = params.market.oracleQuote;
    content.oracleScaleFactor = params.market.oracleScaleFactor;
    content.oracleType = Number(params.market.oracleType);
    content.initialMarginRatio = params.market.initialMarginRatio;
    content.maintenanceMarginRatio = params.market.maintenanceMarginRatio;
    content.makerFeeRate = params.market.makerFeeRate;
    content.takerFeeRate = params.market.takerFeeRate;
    content.minPriceTickSize = params.market.minPriceTickSize;
    content.minQuantityTickSize = params.market.minQuantityTickSize;
    content.minNotional = params.market.minNotional;
    content.reduceMarginRatio = params.market.reduceMarginRatio;
    if (params.market.adminInfo) {
        const adminInfo = InjectiveExchangeV2Proposal.AdminInfo.create();
        adminInfo.admin = params.market.adminInfo.admin;
        adminInfo.adminPermissions = params.market.adminInfo.adminPermissions;
        content.adminInfo = adminInfo;
    }
    return InjectiveExchangeV2Proposal.PerpetualMarketLaunchProposal.fromPartial(content);
};
/**
 * @category Messages
 */
export default class MsgSubmitProposalPerpetualMarketLaunchV2 extends MsgBase {
    static fromJSON(params) {
        return new MsgSubmitProposalPerpetualMarketLaunchV2(params);
    }
    toProto() {
        const { params: initialParams } = this;
        const params = {
            ...initialParams,
            market: {
                ...initialParams.market,
                initialMarginRatio: toChainFormat(initialParams.market.initialMarginRatio).toFixed(),
                maintenanceMarginRatio: toChainFormat(initialParams.market.maintenanceMarginRatio).toFixed(),
                makerFeeRate: toChainFormat(initialParams.market.makerFeeRate).toFixed(),
                minPriceTickSize: toChainFormat(initialParams.market.minPriceTickSize).toFixed(),
                takerFeeRate: toChainFormat(initialParams.market.takerFeeRate).toFixed(),
                minQuantityTickSize: toChainFormat(initialParams.market.minQuantityTickSize).toFixed(),
                minNotional: toChainFormat(initialParams.market.minNotional).toFixed(),
                reduceMarginRatio: toChainFormat(initialParams.market.reduceMarginRatio).toFixed(),
            },
        };
        const depositParams = CosmosBaseV1Beta1Coin.Coin.create();
        depositParams.denom = params.deposit.denom;
        depositParams.amount = params.deposit.amount;
        const contentAny = GoogleProtobufAny.Any.create();
        contentAny.typeUrl = '/injective.exchange.v2.PerpetualMarketLaunchProposal';
        contentAny.value =
            InjectiveExchangeV2Proposal.PerpetualMarketLaunchProposal.encode(createPerpetualMarketLaunch(params)).finish();
        const message = CosmosGovV1Beta1Tx.MsgSubmitProposal.create();
        message.content = contentAny;
        message.initialDeposit = [depositParams];
        message.proposer = params.proposer;
        return CosmosGovV1Beta1Tx.MsgSubmitProposal.fromPartial(message);
    }
    toData() {
        const proto = this.toProto();
        return {
            '@type': '/cosmos.gov.v1beta1.MsgSubmitProposal',
            ...proto,
        };
    }
    toAmino() {
        const { params } = this;
        const content = createPerpetualMarketLaunch(params);
        const messageWithProposalType = snakecaseKeys({
            content: {
                type: 'exchange/PerpetualMarketLaunchProposal',
                value: content,
            },
            initial_deposit: [
                {
                    denom: params.deposit.denom,
                    amount: params.deposit.amount,
                },
            ],
            proposer: params.proposer,
        });
        return {
            type: 'cosmos-sdk/MsgSubmitProposal',
            value: messageWithProposalType,
        };
    }
    toWeb3Gw() {
        const amino = this.toAmino();
        const { value } = amino;
        const messageWithProposalType = {
            ...value,
            content: {
                '@type': '/injective.exchange.v2.PerpetualMarketLaunchProposal',
                ...value.content.value,
            },
        };
        return {
            '@type': '/cosmos.gov.v1beta1.MsgSubmitProposal',
            ...messageWithProposalType,
        };
    }
    toEip712() {
        const { params } = this;
        const amino = this.toAmino();
        const { value, type } = amino;
        const messageAdjusted = {
            ...value,
            content: {
                type: 'exchange/PerpetualMarketLaunchProposal',
                value: {
                    ...value.content.value,
                    initial_margin_ratio: toChainFormat(params.market.initialMarginRatio).toFixed(),
                    maintenance_margin_ratio: toChainFormat(params.market.maintenanceMarginRatio).toFixed(),
                    maker_fee_rate: toChainFormat(params.market.makerFeeRate).toFixed(),
                    taker_fee_rate: toChainFormat(params.market.takerFeeRate).toFixed(),
                    min_price_tick_size: toChainFormat(params.market.minPriceTickSize).toFixed(),
                    min_notional: toChainFormat(params.market.minNotional).toFixed(),
                    min_quantity_tick_size: toChainFormat(params.market.minQuantityTickSize).toFixed(),
                    reduce_margin_ratio: toChainFormat(params.market.reduceMarginRatio).toFixed(),
                },
            },
        };
        return {
            type,
            value: messageAdjusted,
        };
    }
    toEip712V2() {
        const { params } = this;
        const web3gw = this.toWeb3Gw();
        const content = web3gw.content;
        const messageAdjusted = {
            ...web3gw,
            content: {
                ...content,
                oracle_type: InjectiveOracleV1Beta1Oracle.oracleTypeToJSON(Number(content.oracle_type)),
                initial_margin_ratio: numberToCosmosSdkDecString(params.market.initialMarginRatio),
                maintenance_margin_ratio: numberToCosmosSdkDecString(params.market.maintenanceMarginRatio),
                maker_fee_rate: numberToCosmosSdkDecString(params.market.makerFeeRate),
                taker_fee_rate: numberToCosmosSdkDecString(params.market.takerFeeRate),
                min_price_tick_size: numberToCosmosSdkDecString(params.market.minPriceTickSize),
                min_notional: numberToCosmosSdkDecString(params.market.minNotional),
                min_quantity_tick_size: numberToCosmosSdkDecString(params.market.minQuantityTickSize),
                admin_info: content.admin_info || null,
                reduce_margin_ratio: numberToCosmosSdkDecString(params.market.reduceMarginRatio),
            },
        };
        return messageAdjusted;
    }
    toDirectSign() {
        const proto = this.toProto();
        return {
            type: '/cosmos.gov.v1beta1.MsgSubmitProposal',
            message: proto,
        };
    }
    toBinary() {
        return CosmosGovV1Beta1Tx.MsgSubmitProposal.encode(this.toProto()).finish();
    }
}
